import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { useSettings } from "../context/SettingsContext";
import LeadForm from "./LeadForm";

const KEY = "gnons_popup_shown";

export default function EnquiryPopup() {
  const { content } = useSettings();
  const cfg = content.popup || {};
  const [open, setOpen] = useState(false);
  const shown = useRef(false);

  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;

  useEffect(() => {
    if (!cfg.enabled) return;
    if (sessionStorage.getItem(KEY)) return;
    if (window.location.pathname.startsWith("/admin")) return;

    const trigger = () => {
      if (shown.current || sessionStorage.getItem(KEY)) return;
      shown.current = true;
      sessionStorage.setItem(KEY, "1");
      setOpen(true);
      cleanup();
    };

    const delay = (isMobile ? cfg.mobile_delay : cfg.desktop_delay) || 25;
    const scrollPct = (isMobile ? cfg.mobile_scroll : cfg.desktop_scroll) || 50;

    const timer = setTimeout(trigger, delay * 1000);
    const onScroll = () => {
      const sc = (window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100;
      if (sc >= scrollPct) trigger();
    };
    const onExit = (e) => { if (e.clientY <= 0) trigger(); };
    window.addEventListener("scroll", onScroll, { passive: true });
    if (!isMobile && cfg.exit_intent) document.addEventListener("mouseleave", onExit);

    function cleanup() {
      clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("mouseleave", onExit);
    }
    return cleanup;
  }, [cfg.enabled, cfg.desktop_delay, cfg.mobile_delay, cfg.desktop_scroll, cfg.mobile_scroll, cfg.exit_intent]);

  useEffect(() => { document.body.style.overflow = open ? "hidden" : ""; }, [open]);

  if (!cfg.enabled) return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-[95] bg-ink/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setOpen(false)}>
          <motion.div data-testid="enquiry-popup" className="bg-white w-full sm:max-w-3xl rounded-t-xl2 sm:rounded-xl2 overflow-hidden max-h-[94vh] overflow-y-auto grid sm:grid-cols-2" initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }} onClick={(e) => e.stopPropagation()}>
            <div className="relative hidden sm:block bg-beige">
              {cfg.image && <img src={cfg.image} alt="GNONS project" className="absolute inset-0 w-full h-full object-cover" referrerPolicy="no-referrer" />}
            </div>
            <div className="p-6 md:p-7 relative">
              <button onClick={() => setOpen(false)} data-testid="popup-close" className="absolute top-3 right-3 w-9 h-9 rounded-full hover:bg-sand flex items-center justify-center"><X size={18} /></button>
              <div className="kicker mb-2">GNONS</div>
              <h3 className="text-xl md:text-2xl font-bold text-ink leading-tight">{cfg.heading || "Let's Discuss Your Project"}</h3>
              <p className="text-sm text-muted mt-2 mb-5">{cfg.text}</p>
              <LeadForm sourcePage="Website Enquiry Popup" onDone={() => setOpen(false)} />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
